/**
 * sl-728o.1.1: TutorialPersistence Service
 *
 * Manages tutorial completion state in localStorage with reactive updates.
 * Handles migration from old coachmark keys.
 */

import { LiveData, Service } from '@toeverything/infra';

import type { TutorialProgress, TutorialState } from '../types';

const STORAGE_KEY = 'streamlain-tutorials';

// Old keys to migrate from
const OLD_TOUR_KEY = 'onboarding:tour-seen';
const OLD_COACHMARKS_KEY = 'streamlain-coachmarks-seen';

export class TutorialPersistence extends Service {
  readonly states$ = new LiveData<TutorialProgress>(this.load());

  constructor() {
    super();
    this.migrate();
  }

  // --- Public API ---

  getState(sessionId: string): TutorialState {
    return this.states$.value.states[sessionId] ?? 'not_seen';
  }

  isCompleted(sessionId: string): boolean {
    return this.getState(sessionId) === 'completed';
  }

  isDeferred(sessionId: string): boolean {
    return this.getState(sessionId) === 'deferred';
  }

  markCompleted(sessionId: string): void {
    this.setState(sessionId, 'completed');
  }

  markDeferred(sessionId: string): void {
    this.setState(sessionId, 'deferred');
  }

  getProgress(sessionId: string): number {
    return this.states$.value.progress[sessionId] ?? 0;
  }

  setProgress(sessionId: string, stepIndex: number): void {
    const current = this.states$.value;
    const updated: TutorialProgress = {
      ...current,
      progress: { ...current.progress, [sessionId]: stepIndex },
    };
    this.states$.next(updated);
    this.save(updated);
  }

  reset(sessionId: string): void {
    const current = this.states$.value;
    const { [sessionId]: _state, ...restStates } = current.states;
    const { [sessionId]: _progress, ...restProgress } = current.progress;
    const updated: TutorialProgress = {
      states: restStates,
      progress: restProgress,
    };
    this.states$.next(updated);
    this.save(updated);
  }

  resetAll(): void {
    const empty: TutorialProgress = { states: {}, progress: {} };
    this.states$.next(empty);
    this.save(empty);
  }

  // --- Private ---

  private setState(sessionId: string, state: TutorialState): void {
    const current = this.states$.value;
    const updated: TutorialProgress = {
      ...current,
      states: { ...current.states, [sessionId]: state },
    };
    this.states$.next(updated);
    this.save(updated);
  }

  private load(): TutorialProgress {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<TutorialProgress>;
        return {
          states: parsed.states ?? {},
          progress: parsed.progress ?? {},
        };
      }
    } catch {
      // Ignore parse errors
    }
    return { states: {}, progress: {} };
  }

  private save(data: TutorialProgress): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignore storage errors (quota, etc.)
    }
  }

  /**
   * Migrate from old localStorage keys.
   *
   * - 'onboarding:tour-seen' → states['onboarding-welcome'] = 'completed'
   * - 'streamlain-coachmarks-seen' (JSON array) → states['automation:{id}'] = 'completed'
   *
   * Old keys are removed after successful migration.
   */
  private migrate(): void {
    let needsSave = false;
    const current = this.states$.value;
    const updatedStates = { ...current.states };

    // Migrate old tour key
    try {
      const tourSeen = localStorage.getItem(OLD_TOUR_KEY);
      if (tourSeen === 'true') {
        if (!updatedStates['onboarding-welcome']) {
          updatedStates['onboarding-welcome'] = 'completed';
          needsSave = true;
        }
        localStorage.removeItem(OLD_TOUR_KEY);
      }
    } catch {
      // Ignore
    }

    // Migrate old coachmark keys
    try {
      const coachmarksSeen = localStorage.getItem(OLD_COACHMARKS_KEY);
      if (coachmarksSeen) {
        const ids = JSON.parse(coachmarksSeen) as string[];
        for (const id of ids) {
          const sessionId = `automation:${id}`;
          if (!updatedStates[sessionId]) {
            updatedStates[sessionId] = 'completed';
            needsSave = true;
          }
        }
        localStorage.removeItem(OLD_COACHMARKS_KEY);
      }
    } catch {
      // Ignore parse errors
    }

    if (needsSave) {
      const updated: TutorialProgress = {
        ...current,
        states: updatedStates,
      };
      this.states$.next(updated);
      this.save(updated);
    }
  }
}
